/**
 * event-members.js
 */

import React from 'react';
import { Link, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Select from 'components/form-inputs/select';
import Icon from 'components/icon';
import EventMemberControl from './event-member-control';
import { TrainingGroupsContext } from 'routing/routes/config/groups/training/training-groups-context';


const useStyles = makeStyles(theme => ({ 
  note: {
    margin: `${theme.spacing(1.5)} 0`,
    color: theme.palette.sys.onSurfaceVariant
  },
  numMembers: {
    height: '100%',
    color: theme.palette.sys.onSurfaceVariant,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-0.5),
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.warning.main,
      marginLeft: theme.spacing(.5),
    }    
  },
}));


export default function EventEditMembers(props) {
  const {
    drawerState,
    translation, 
  } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const [triggerOpenAccordion, setTriggerOpenAccordion] = React.useState(0);
  const { control, formState } = useFormContext();
  const { groups } = React.useContext(TrainingGroupsContext);  
  const groupId = useWatch({ control, name: 'groupId' });
  const membersValue = useWatch({ control, name: 'members' });
  const numMembers = membersValue ? membersValue.length : 0;
  const membersErrors = formState.errors?.members;


  // if there are any member errors, open the accordion
  React.useEffect(() => {
    if (Boolean(membersErrors)) {
      setTriggerOpenAccordion(p => p + 1);
    }
  }, [membersErrors]);


  // is the member list out of sync with the selected group?
  const isGroupUnsynced = React.useMemo(() => {
    if (groupId <= 0) {
      // no selected group
      return false;
    }

    const group = groups.find(g => g.id === groupId);
    if (group.members.length !== membersValue.length) {
      // membership count not equal
      return true;
    }
    for (const localMember of membersValue) {
      const groupMember = group.members.find(x => (x.id === localMember.id));
      if (!groupMember) {
        // mebership not equal
        return true;
      }
      if (localMember.isLeader !== groupMember.isLeader) {
        // leadership not equal
        return true;
      }
    }

    // default
    return false;
  }, [groups, groupId, membersValue]);


  return (
    <EditorIconSection
      iconName='group'
      helpProps={{
        translation,
        section: 'members'
      }}
    >
      <EditorAccordion
        shiftButton
        name={t('members.sectionName')}
        expand={triggerOpenAccordion}
        summary={<>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('members.group.helpTitle')}
            text={t('members.group.helpInfo')}
          >
            {focusHandlers =>
              <FormGridContainer style={{ paddingTop: 4 }}>
                <FormGridItem md={7} style={{ width: 0 }}> {/* width: 0 somehow fixes the select overflow problem. shrug. */}
                  {/* prevent autocomplete interaction from opening accordion */}
                  <div onClick={e => e.stopPropagation()}>
                    <Select
                      name='groupId'
                      label={t('members.group.label')}
                      supportNull
                      focusHandlers={focusHandlers}
                      onChange={(e, v) => {
                        if (v === null) {
                          setTriggerOpenAccordion(p => p + 1);      
                        }
                      }}
                      options={[
                        {
                          id: null,
                          name: t('members.group.customLabel'),
                        },
                        ...groups.map(g => ({
                          ...g,
                          name: `${g.name} (${g.members.length})`
                        }))
                      ]}
                    />
                  </div>
                </FormGridItem>
                <FormGridItem md={5}>
                  <div className={`flex-hc ${classes.numMembers}`}>
                    <Typography variant='caption'>
                      {numMembers} {`Handler${numMembers === 1 ? '' : 's'}`}
                    </Typography>
                    {isGroupUnsynced &&
                      <Tooltip arrow placement='top' title='Member list is out of sync with selected group'>
                        <span><Icon type='sync-problem' fontSize='small' /></span>
                      </Tooltip>
                    }
                  </div>
                </FormGridItem>
              </FormGridContainer>}
          </HelpTextItem>
        </>}

        detail={<>
          {/* event members detail */}
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('members.members.helpTitle')}
            text={t('members.members.helpInfo')}
          >
            <EventMemberControl 
              isGroupUnsynced={isGroupUnsynced} 
              {...props} 
            />
            <Typography className={classes.note}>
              Use the <Link component={RouterLink} to='/groups' underline="hover">Groups page</Link> to invite handlers that you're not yet connected with.
            </Typography>
          </HelpTextItem>
        </>}
      />
    </EditorIconSection>
  );
}